import { useEffect, useState } from "react";
import Select from "../../../components/Form/Select"
import Modal from "../../../components/Modal/Modal"
import Button from "../../../components/ButtonGroup/Button";
import { getBrands, createInsurerOffer, setAssuranceAsResponded, getInsurerDetails } from "../../../services/insurer";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { pushNotification } from "../../../store/actions/Notifications";
import { Notification } from "../../../models/Notification";

export default function NewOfferModal({isModalOpen, setIsModalOpen, userAssuranceId}) {
    const [form, setForm] = useState({offerType: 'PHONE', priceOption: 'annually'});
    const [brands, setBrands] = useState(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [insurerDetails, setInsurerDetails] = useState(null);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { user, roles } = useSelector(store => store.session);
    const { t } = useTranslation(['insurer', 'offerType']);

    useEffect(() => {
        if (!brands) {
            getBrands().subscribe({
            next: (brands) => {
              setBrands(brands.brands);

              if (!roles.find(role => role.role === 'ROLE_ADMIN') && brands.brands.length > 0) {
                setForm({...form, brandId: brands.brands[0].id});
              }
            }
          });
        }

        if (!insurerDetails) {
            getInsurerDetails().subscribe({
                next: (insurerDetails) => {
                    console.log(insurerDetails.insurerDetails);
                    setInsurerDetails(insurerDetails.insurerDetails);
                }
            });
        }
      }, [isModalOpen])

    const disabled = !form.brandId || !form.price || !form.expirationDate || !form.description || !userAssuranceId || (insurerDetails && insurerDetails.offerCountRemaining === 0);


    const handleCreateInsurerOffer = () => {
        if (disabled || !brands || brands.length === 0) {
            return;
        }

        setIsLoading(true);
        setError(null);

        const price = form.priceOption === 'annually' ? parseInt(parseFloat(form.price) * 100) : parseInt(parseFloat(form.price) * 100 * 12);

        createInsurerOffer({brandId: form.brandId, priceAnually: price,
             expirationDate: form.expirationDate, userAssuranceId: userAssuranceId, description: form.description,
             offerType: form.offerType, phone: form.phone, link: form.link, submissionNumber: form.submissionNumber}).subscribe({
            next: (response) => {
                setAssuranceAsResponded(userAssuranceId).subscribe({
                    next: (response) => {
                        dispatch(pushNotification(new Notification(Notification.SUCCESS, 'Success', 'Insurance offer created successfully.')));
                        navigate('/Insurer');
                        setIsModalOpen(false);
                        setIsLoading(false);
                    },
                    error: (error) => {
                        console.error('Error setting assurance as responded:', error);
                        dispatch(pushNotification(new Notification(Notification.ERROR, 'Error', 'Offer created but failed to update status. Please contact support.')));
                        setIsLoading(false);
                    }
                });
            },
            error: (error) => {
                setIsLoading(false);
                setError(error);
                
                let errorMessage = 'Failed to create insurance offer. Please try again.';
                
                if (error.response) {
                    const status = error.response.status;
                    const data = error.response.data;

                    if (status >= 400 && status < 500) {
                        if (data.error.code === 37) {
                            errorMessage = 'An offer was already created for this assurance with the same group.';
                        } else if (data.error.code === 36) {
                            errorMessage = 'The offer could not be created because you have reached the maximum number of offers for the last 30 days.';
                        }
                    }
                } else if (error.request) {
                    errorMessage = 'Network error. Please check your connection and try again.';
                }
                
                dispatch(pushNotification(new Notification(Notification.ERROR, 'Error', errorMessage)));
            }
        });
    }

    const offerTypeOptions = [
        {id: 'PHONE', name: t('offerType:phone')},
        {id: 'LINK', name: t('offerType:link')},
        {id: 'AUTHORISATION', name: t('offerType:authorisation')},
    ]

    const priceOptions = [
        { id: 'annually', name: t('offerType:annually') },
        { id: 'monthly', name: t('offerType:monthly') },
    ];

    return (
        <Modal title="New offer" isOpen={isModalOpen} onClose={() => {setIsModalOpen(false)}}>
            
            {roles.find(role => role.role === 'ROLE_ADMIN') && <Select onSelect={(value) => setForm({...form, brandId: value.id})} label="Select brand" options={brands} />}
            <div className="mt-4">
                <Select onSelect={(value) => setForm({...form, offerType: value.id})} label={t('offerType:label')} options={offerTypeOptions} />    
            </div>

            <div className="flex flex-col mt-4">
                <span className="text-base font-medium text-gray-900">{t('offerType:submissionNumber')}</span>
                <input
                    onChange={(e) => setForm({...form, submissionNumber: e.target.value})}
                    type="text"
                    name="submissionNumber"
                    id="submissionNumber"
                    className="shadow-sm focus:ring-neatcar focus:border-neatcar block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="#15020-300-203"
                />
            </div>

            <div className="flex flex-col mt-4">
                <span className="text-base font-medium text-gray-900">{t('offerType:phone')}</span>
                <input
                    onChange={(e) => setForm({...form, phone: e.target.value})}
                    type="text"
                    name="phone"
                    id="phone"
                    className="shadow-sm focus:ring-neatcar focus:border-neatcar block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="+1-800-123-4567"
                />
            </div>

            <div className="flex flex-col mt-4">
                <span className="text-base font-medium text-gray-900">{t('offerType:link')}</span>
                <input
                    onChange={(e) => setForm({...form, link: e.target.value})}
                    type="text"
                    name="link"
                    id="link"
                    className="shadow-sm focus:ring-neatcar focus:border-neatcar block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="https://www.neatcover.ca"
                />
            </div>
            
            <div className="flex flex-col mt-4">
                <span className="text-base font-medium text-gray-900">{t('offerType:price')}</span>
                <div className="flex flex-row items-center">
                    <div className="flex-1 flex flex-row h-12 mt-1.5">
                        <input
                            onChange={(e) => setForm({...form, price: e.target.value})}
                            type="number"
                            name="price"
                            id="price"
                            className="shadow-sm focus:ring-neatcar focus:border-neatcar block w-full sm:text-sm border-gray-300 rounded-md"
                            placeholder="2499.99"
                        />
                    </div>
                    <div className="w-32 ml-4">
                        <Select onSelect={(value) => setForm({...form, priceOption: value.id})} options={priceOptions} />
                    </div>
                </div>
                
            </div>

            <div className="flex flex-col mt-4">
                <span className="text-base font-medium text-gray-900">{t('offerType:expirationDate')}</span>
                <input
                    onChange={(e) => setForm({...form, expirationDate: e.target.value})}
                    type="date"
                    name="expirationDate"
                    id="expirationDate"
                    className="shadow-sm focus:ring-neatcar focus:border-neatcar block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="2025-01-01"
                />
            </div>

            <div className="flex flex-col mt-4">
                <span className="text-base font-medium text-gray-900">{t('offerType:description')}</span>
                <textarea
                    onChange={(e) => setForm({...form, description: e.target.value})}
                    type="text"
                    name="description"
                    id="description"
                    className="shadow-sm focus:ring-neatcar focus:border-neatcar block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="Description"
                />
            </div>

            <div className="flex justify-end mt-4">
                <Button disabled={disabled} onClick={handleCreateInsurerOffer}>
                    {t('offerType:sendOffer')}
                </Button>
            </div>
      </Modal>
    )
}       